import React, { useEffect, useState, useContext, useMemo } from "react";
import { SunsetAPIContext } from "../../Networking/ApiProvider";
import { LeaderboardData } from "../../Networking/SunsetAPI";
import styles from "./Leaderboard.module.css";

const STAT_DISPLAY_NAMES: Record<string, string> = {
  bags_collected: "Bags Collected",
  ems_revived: "EMS Revived",
  ems_treat: "EMS Treatments",
  ems_heal: "EMS Heals",
  trucker_loads: "Trucker Loads",
  train_jobs: "Train Jobs",
  full_garbage_drop: "Garbage Drop-offs",
  bus_routes: "Bus Routes",
  revives_received: "Revives Received",
  player_deaths: "Player Deaths",
  dist_travelled_foot: "Distance Travelled (Foot)",
  dist_travelled_car: "Distance Travelled (Car)",
  dist_travelled_bike: "Distance Travelled (Bike)",
  dist_travelled_boat: "Distance Travelled (Boat)",
  dist_travelled_air: "Distance Travelled (Air)",
  veh_destroyed_car: "Vehicles Destroyed (Car)",
  veh_destroyed_boat: "Vehicles Destroyed (Boat)",
  veh_destroyed_air: "Vehicles Destroyed (Air)",
  vehicles_picklocked: "Vehicles Picklocked",
  fuel_consumed: "Fuel Consumed",
  mech_repairs_done: "Mechanic Repairs",
  ores_mined: "Ores Mined",
  fish_caught: "Fish Caught",
  people_killed: "People Killed",
  criminals_killed: "Criminals Killed",
  gang_members_killed: "Gang Members Killed",
  police_killed: "Police Killed",
  headshots: "Headshots",
  bullets_fired: "Bullets Fired",
  bullets_hit: "Bullets Hit",
  knife_kills: "Knife Kills",
  bandana_kills: "Bandana Kills",
  total_wanted_stars: "Total Wanted Stars",
  wanted_stars_evaded: "Wanted Stars Evaded",
  times_wanted: "Times Wanted",
  times_arrested: "Times Arrested",
  suspect_deaths: "Suspect Deaths",
  jew_heists_success: "Jewelry Heists",
  museum_heists_success: "Museum Heists",
  stores_robbed: "Stores Robbed",
  atms_robbed: "ATMs Robbed",
  players_robbed: "Players Robbed",
  money_robbed: "Money Robbed",
  weed_sold: "Weed Sold",
  meth_sold: "Meth Sold",
  heroin_sold: "Heroin Sold",
  coke_sold: "Coke Sold",
  ieds_detonated: "IEDs Detonated",
  turfs_captured: "Turfs Captured",
  turf_kills: "Turf Kills",
  turf_deaths: "Turf Deaths",
  police_duty_time: "Police Duty Time",
  arrests_made: "Arrests Made",
  fines_issued: "Fines Issued",
  panic_buttons: "Panic Buttons",
  tactical_breaches: "Tactical Breaches",
  suspects_traced: "Suspects Traced",
  police_barriers: "Police Barriers",
  seized_total: "Total Seized",
  seized_weed: "Weed Seized",
  seized_heroin: "Heroin Seized",
  seized_meth: "Meth Seized",
  seized_coke: "Coke Seized",
  deaths_on_duty: "Deaths on Duty",
  fire_missions: "Fire Missions",
  ems_barriers: "EMS Barriers",
};

const STAT_CATEGORIES: Record<string, string[]> = {
  "Jobs": [
    "bags_collected", "trucker_loads", "train_jobs", "full_garbage_drop",
    "bus_routes", "fire_missions", "ores_mined", "fish_caught",
  ],
  "EMS": [
    "ems_revived", "ems_treat", "ems_heal", "ems_barriers",
  ],
  "Combat": [
    "people_killed", "criminals_killed", "gang_members_killed", "police_killed",
    "headshots", "bullets_fired", "bullets_hit", "knife_kills", "bandana_kills",
  ],
  "Deaths": [
    "player_deaths", "revives_received", "suspect_deaths", "deaths_on_duty",
  ],
  "Crime": [
    "total_wanted_stars", "wanted_stars_evaded", "times_wanted", "times_arrested",
    "jew_heists_success", "museum_heists_success", "stores_robbed", "atms_robbed",
    "players_robbed", "money_robbed", "ieds_detonated",
  ],
  "Drugs": [
    "weed_sold", "meth_sold", "heroin_sold", "coke_sold",
  ],
  "Vehicles": [
    "dist_travelled_foot", "dist_travelled_car", "dist_travelled_bike",
    "dist_travelled_boat", "dist_travelled_air", "veh_destroyed_car",
    "veh_destroyed_boat", "veh_destroyed_air", "vehicles_picklocked",
    "fuel_consumed", "mech_repairs_done",
  ],
  "Turfs": [
    "turfs_captured", "turf_kills", "turf_deaths",
  ],
  "Police": [
    "police_duty_time", "arrests_made", "fines_issued", "panic_buttons",
    "tactical_breaches", "suspects_traced", "police_barriers",
    "seized_total", "seized_weed", "seized_heroin", "seized_meth", "seized_coke",
  ],
};

const CATEGORY_NAMES = Object.keys(STAT_CATEGORIES);

function getDisplayName(statKey: string): string {
  return STAT_DISPLAY_NAMES[statKey] || statKey.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function getRankIcon(rank: number): React.ReactNode {
  if (rank === 1) return <img src="/pixel-icons/medal-reward-Original.png" alt="1st" className={styles.rankMedal} />;
  if (rank === 2) return <img src="/pixel-icons/silver-medal-2-Original.png" alt="2nd" className={styles.rankMedal} />;
  if (rank === 3) return <img src="/pixel-icons/bronze-medal-2-Original.png" alt="3rd" className={styles.rankMedal} />;
  return <span className={styles.rankNumber}>{rank}</span>;
}

function formatValue(statKey: string, value: number): string {
  if (statKey === "police_duty_time") {
    const hours = Math.floor(value / 60);
    const mins = value % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }
  if (statKey.startsWith("dist_travelled_")) {
    return value.toLocaleString() + "m";
  }
  if (statKey === "money_robbed") {
    return "$" + value.toLocaleString();
  }
  return value.toLocaleString();
}

const LeaderboardPage: React.FC = () => {
  const api = useContext(SunsetAPIContext);
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(CATEGORY_NAMES[0]);

  useEffect(() => {
    const fetchData = async () => {
      if (!api) return;
      setLoading(true);
      const response = await api.getLeaderboard();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || "Failed to load leaderboard.");
      }
      setLoading(false);
    };
    fetchData();
  }, [api]);

  const activeStats = useMemo(() => {
    if (!data) return [];

    const statKeys = STAT_CATEGORIES[activeCategory];
    if (!statKeys) return [];

    return statKeys
      .filter((key) => key in data)
      .map((key) => ({ key, entries: data[key] }));
  }, [data, activeCategory]);

  return (
    <div className={`card ${styles.wrapper}`}>
      <h2 className={styles.pageTitle}>Leaderboard</h2>
      <p className={styles.pageSubtitle}>
        Top characters across all tracked statistics.
      </p>

      {loading && <div className={styles.loading}>Loading leaderboard...</div>}
      {error && <div className={styles.error}>{error}</div>}

      {!loading && !error && data && (
        <>
          <div className={styles.categoryTabs}>
            {CATEGORY_NAMES.map((cat) => (
              <button
                key={cat}
                className={`${styles.categoryTab} ${activeCategory === cat ? styles.categoryTabActive : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className={styles.categoryGrid}>
            {activeStats.length === 0 ? (
              <p className={styles.emptyState}>No data available for this category.</p>
            ) : (
              activeStats.map(({ key, entries }) => (
                <div key={key} className={styles.categoryCard}>
                  <h4 className={styles.categoryTitle}>{getDisplayName(key)}</h4>
                  {entries.length === 0 ? (
                    <p className={styles.emptyState}>No data available</p>
                  ) : (
                    <table className={styles.leaderboardTable}>
                      <thead>
                        <tr>
                          <th style={{ width: "36px" }}>#</th>
                          <th>Character</th>
                          <th style={{ textAlign: "right", width: "70px" }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, index) => (
                          <tr key={entry.user_id}>
                            <td className={styles.rank}>
                              {getRankIcon(index + 1)}
                            </td>
                            <td className={styles.characterName}>
                              {entry.character_name.replace(/_/g, " ")}
                            </td>
                            <td className={styles.statValue}>
                              {formatValue(key, entry.stat_value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LeaderboardPage;
